import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import api from '../../../utils/api';

const KeyMetrics = ({ metrics, product, movementData: propMovementData, turnoverData: propTurnoverData }) => {
  const [movementData, setMovementData] = useState([]);
  const [turnoverData, setTurnoverData] = useState([]);

  useEffect(() => {
    const calculateChartData = async () => {
      if (!product?.barcode) {
        // Set default empty data if no barcode
        setMovementData([
          { month: 'Jan', inbound: 0, outbound: 0 },
          { month: 'Feb', inbound: 0, outbound: 0 },
          { month: 'Mar', inbound: 0, outbound: 0 },
          { month: 'Apr', inbound: 0, outbound: 0 },
          { month: 'May', inbound: 0, outbound: 0 },
          { month: 'Jun', inbound: 0, outbound: 0 }
        ]);
        setTurnoverData([
          { period: 'Q1', turnover: 0 },
          { period: 'Q2', turnover: 0 },
          { period: 'Q3', turnover: 0 },
          { period: 'Q4', turnover: 0 }
        ]);
        return;
      }

      try {
        // Fetch checkout data for the specific product
        const checkoutResponse = await api.get('/checkouts');
        const productCheckouts = checkoutResponse.data.filter(item => 
          item.barcode === product.barcode || item.sku === product.barcode
        );

        // Calculate monthly movement data (last 6 months)
        const now = new Date();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthlyData = [];

        for (let i = 5; i >= 0; i--) {
          const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
          const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

          const monthSales = productCheckouts.filter(item => {
            const itemDate = new Date(item.createdAt?.toDate ? item.createdAt.toDate() : item.createdAt);
            return itemDate >= monthStart && itemDate <= monthEnd;
          });

          const outbound = monthSales.reduce((sum, item) => sum + (item.quantity || 0), 0);
          
          monthlyData.push({
            month: monthNames[monthDate.getMonth()],
            inbound: 0, // We don't have purchase data in this structure
            outbound: outbound
          });
        }

        setMovementData(monthlyData);

        // Calculate quarterly turnover data
        const quarterlyData = [];
        const currentYear = now.getFullYear();
        
        for (let quarter = 1; quarter <= 4; quarter++) {
          const quarterStart = new Date(currentYear, (quarter - 1) * 3, 1);
          const quarterEnd = new Date(currentYear, quarter * 3, 0);
          
          const quarterSales = productCheckouts.filter(item => {
            const itemDate = new Date(item.createdAt?.toDate ? item.createdAt.toDate() : item.createdAt);
            return itemDate >= quarterStart && itemDate <= quarterEnd;
          });
          
          const totalSold = quarterSales.reduce((sum, item) => sum + (item.quantity || 0), 0);
          const avgStock = product.totalStock || 1; // Avoid division by zero
          const turnover = avgStock > 0 ? (totalSold / avgStock) * 4 : 0; // Annualized
          
          quarterlyData.push({
            period: `Q${quarter}`,
            turnover: Math.round(turnover * 10) / 10 // Round to 1 decimal
          });
        }

        setTurnoverData(quarterlyData);

      } catch (error) {
        console.error('Error calculating chart data:', error);
        // Set default data on error
        setMovementData([
          { month: 'Jan', inbound: 0, outbound: 0 },
          { month: 'Feb', inbound: 0, outbound: 0 },
          { month: 'Mar', inbound: 0, outbound: 0 },
          { month: 'Apr', inbound: 0, outbound: 0 },
          { month: 'May', inbound: 0, outbound: 0 },
          { month: 'Jun', inbound: 0, outbound: 0 }
        ]);
        setTurnoverData([
          { period: 'Q1', turnover: 0 },
          { period: 'Q2', turnover: 0 },
          { period: 'Q3', turnover: 0 },
          { period: 'Q4', turnover: 0 }
        ]);
      }
    };

    calculateChartData();
  }, [product]);

  const getMetricIcon = (type) => {
    switch (type) {
      case 'movement': return 'TrendingUp';
      case 'supply': return 'Calendar';
      case 'turnover': return 'RotateCw';
      case 'velocity': return 'Zap';
      default: return 'BarChart3';
    }
  };

  const getMetricColor = (type) => {
    switch (type) {
      case 'movement': return 'text-success bg-success/10';
      case 'supply': return 'text-warning bg-warning/10';
      case 'turnover': return 'text-primary bg-primary/10';
      case 'velocity': return 'text-secondary bg-secondary/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatValue = (value, type) => {
    switch (type) {
      case 'movement':
        return `${value} units/month`;
      case 'supply':
        return `${value} days`;
      case 'turnover':
        return `${value}x per year`;
      case 'velocity':
        return `${value} units/day`;
      default:
        return value;
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return 'TrendingUp';
      case 'down': return 'TrendingDown';
      case 'stable': return 'Minus';
      default: return 'Minus';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up': return 'text-success';
      case 'down': return 'text-destructive';
      case 'stable': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="BarChart3" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Key Metrics</h2>
          <p className="text-sm text-muted-foreground">Performance indicators and trends</p>
        </div>
      </div>
      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {metrics?.map((metric) => (
          <div key={metric?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getMetricColor(metric?.type)}`}>
                <Icon name={getMetricIcon(metric?.type)} size={16} />
              </div>
              <div className={`flex items-center gap-1 ${getTrendColor(metric?.trend)}`}>
                <Icon name={getTrendIcon(metric?.trend)} size={14} />
                <span className="text-xs font-medium">{metric?.change}</span>
              </div>
            </div>
            <div className="text-2xl font-semibold text-foreground mb-1">
              {metric?.value}
            </div>
            <div className="text-xs text-muted-foreground">
              {metric?.label}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              vs last period
            </div>
          </div>
        ))}
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Movement Chart */}
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <h3 className="font-medium text-foreground mb-4">Inventory Movement</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={movementData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="inbound" fill="var(--color-success)" name="Inbound" />
                <Bar dataKey="outbound" fill="var(--color-destructive)" name="Outbound" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Turnover Chart */}
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <h3 className="font-medium text-foreground mb-4">Inventory Turnover</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={turnoverData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="period" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="turnover" 
                  stroke="var(--color-primary)" 
                  strokeWidth={2}
                  dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                  name="Turnover Rate"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Additional Insights */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Target" size={16} className="text-primary" />
            <h4 className="font-medium text-foreground">Performance Status</h4>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Stock Health:</span>
              <span className="text-success font-medium">Optimal</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Reorder Status:</span>
              <span className="text-warning font-medium">Monitor</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Demand Trend:</span>
              <span className="text-success font-medium">Increasing</span>
            </div>
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Lightbulb" size={16} className="text-warning" />
            <h4 className="font-medium text-foreground">Recommendations</h4>
          </div>
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>• Consider increasing reorder point by 10%</p>
            <p>• Monitor seasonal demand patterns</p>
            <p>• Optimize supplier lead times</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyMetrics;